import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader } from 'kl-b2c-ui-kit';
import {
    ConfirmationProvider,
    CourseProvider,
    EventProvider,
    MobileMenuStateProvider,
    ProfessionProvider,
} from 'contexts';
import { AppLayout } from 'containers/app-layout';
import { KidsCoursesProvider } from 'contexts/kids-course.context';

const AboutPage = lazy(() => import('../pages/about'));
const NewsListPage = lazy(() => import('../pages/news-list'));
const NewsItemPage = lazy(() => import('../pages/news-item'));
const EventsList = lazy(() => import('../pages/events-list'));
const EventsItem = lazy(() => import('../pages/events-item'));
const NotFoundPage = lazy(() => import('../pages/not-found'));
const HomePage = lazy(() => import('../pages/home'));
const CourseItem = lazy(() => import('../pages/courses-item'));
const ProfessionItem = lazy(() => import('../pages/profession-item'));
const CourseList = lazy(() => import('../pages/courses-list'));
const KidsCourses = lazy(() => import('../pages/kids-courses'));
const AcademyAlliance2 = lazy(() => import('../pages/academy-alliance'));
const ForBusinesses = lazy(() => import('../pages/for-businesses'));
const ForSchools = lazy(() => import('../pages/for-schools-and-colleges'));
const ForGovernments = lazy(() => import('../pages/for-governments'));
const KidsCyberResilience = lazy(() =>
    import('../pages/kids-cyber-resilience')
);
const PrivacyPolicy = lazy(() => import('../pages/privacy-policy'));
const Confirmation = lazy(() => import('../pages/confirmation'));
const AllianceForm = lazy(() => import('../pages/alliance-form'));

const AppRouter: React.FC = (): JSX.Element => (
    <MobileMenuStateProvider>
        <AppLayout>
            <Suspense fallback={<Loader variant={'fixed'} size={'large'} />}>
                <Routes>
                    <Route
                        path={'/'}
                        element={
                            <EventProvider>
                                <HomePage />
                            </EventProvider>
                        }
                    />
                    <Route
                        path={'/kids-cyber-resilience/:langId?'}
                        element={
                            <EventProvider>
                                <KidsCyberResilience />
                            </EventProvider>
                        }
                    />
                    <Route path={'/about'} element={<AboutPage />} />
                    <Route path={'/news/:id'} element={<NewsItemPage />} />
                    <Route path={'/news'} element={<NewsListPage />} />
                    <Route
                        path={'/academy-alliance'}
                        element={
                            <EventProvider>
                                <AcademyAlliance2 />
                            </EventProvider>
                        }
                    />
                    <Route
                        path={'/events/:id'}
                        element={
                            <EventProvider>
                                <EventsItem />
                            </EventProvider>
                        }
                    />
                    <Route
                        path={'/events'}
                        element={
                            <EventProvider>
                                <EventsList />
                            </EventProvider>
                        }
                    />
                    <Route
                        path={'course/:id'}
                        element={
                            <CourseProvider>
                                <CourseItem />
                            </CourseProvider>
                        }
                    />
                    <Route
                        path={
                            'courses/:skills?/:languages?/:directions?/:studyForms?/:forWhom?'
                        }
                        element={
                            <ProfessionProvider>
                                <CourseProvider>
                                    <CourseList />
                                </CourseProvider>
                            </ProfessionProvider>
                        }
                    />
                    <Route
                        path={'program/:id'}
                        element={
                            <CourseProvider>
                                <ProfessionProvider>
                                    <ProfessionItem />
                                </ProfessionProvider>
                            </CourseProvider>
                        }
                    />
                    <Route
                        path={'for-businesses'}
                        element={
                            <CourseProvider>
                                <ForBusinesses />
                            </CourseProvider>
                        }
                    />
                    <Route
                        path={'for-governments'}
                        element={
                            <CourseProvider>
                                <ForGovernments />
                            </CourseProvider>
                        }
                    />
                    <Route
                        path={'academy-alliance-request-form'}
                        element={<AllianceForm />}
                    />
                    <Route
                        path={'for-kids'}
                        element={
                            <KidsCoursesProvider>
                                <KidsCourses />
                            </KidsCoursesProvider>
                        }
                    />
                    {process.env.REACT_APP_LOCALE === 'ru' && (
                        <Route
                            path={'for-schools-and-colleges'}
                            element={
                                <CourseProvider>
                                    <ForSchools />
                                </CourseProvider>
                            }
                        />
                    )}
                    {process.env.REACT_APP_LOCALE === 'en' && (
                        <Route
                            path={'for-schools-and-colleges'}
                            element={<Navigate to={'/'} replace />}
                        />
                    )}
                    <Route
                        path={'confirmation/:agreementId'}
                        element={
                            <ConfirmationProvider>
                                <Confirmation />
                            </ConfirmationProvider>
                        }
                    />
                    <Route
                        path={'privacy-policy'}
                        element={<PrivacyPolicy />}
                    />
                    <Route path={'*'} element={<NotFoundPage />} />
                </Routes>
            </Suspense>
        </AppLayout>
    </MobileMenuStateProvider>
);
export default AppRouter;
